import { useCallback, useMemo } from 'react'
import {
  VersionReportVersionEntity,
  VersionReportVersionPageEntity,
} from '../../../../domain/entity/version-report/VersionReportVersionEntity'
import { EvaluateResult } from '../../../../domain/entity/version-report/evaluation'
import { useVersionReportVersionRepository } from '../../../../services/adaptors/versionReportVersionRepositoryAdaptor'
import { isAiAvailable } from '../../../../lib/functions/ai'

export const useEvaluate = (
  currentVersion: VersionReportVersionEntity | undefined,
  currentPage: VersionReportVersionPageEntity | undefined
) => {
  const {
    evaluateOverview,
    evaluateProgressByProcess,
    evaluateProgressByTeam,
  } = useVersionReportVersionRepository()

  const buildEvaluateRow = useCallback(
    (targetUuid: string) => {
      if (!currentPage || !currentVersion) {
        return undefined
      }
      switch (currentPage.pageType) {
        case 'PROGRESS_OVERVIEW':
          return async (): Promise<EvaluateResult | undefined> => {
            const response = await evaluateOverview({
              snapshotUuid: currentVersion.snapshotUuid,
              leafUuids: [targetUuid],
            })
            return response.result
              ? { uuid: targetUuid, ...response.result }
              : undefined
          }
        case 'PROGRESS_REPORT_BY_PROCESS':
          return async (): Promise<EvaluateResult | undefined> => {
            if (currentPage.config.type !== 'PROGRESS_REPORT_BY_PROCESS') {
              return undefined
            }
            const response = await evaluateProgressByProcess({
              snapshotUuid: currentVersion.snapshotUuid,
              projectPlanUuids: [targetUuid],
            })
            return response.result?.[0]
          }
        case 'PROGRESS_REPORT_BY_TEAM':
          return async (): Promise<EvaluateResult | undefined> => {
            if (currentPage.config.type !== 'PROGRESS_REPORT_BY_TEAM') {
              return undefined
            }
            const response = await evaluateProgressByTeam({
              snapshotUuid: currentVersion.snapshotUuid,
              teamUuids: [targetUuid],
              rootUuid: currentPage.config.rootUuid,
            })
            return response.result?.[0]
          }
      }
    },
    [
      currentPage,
      currentVersion,
      evaluateOverview,
      evaluateProgressByProcess,
      evaluateProgressByTeam,
    ]
  )

  const evaluateRow = useMemo(() => {
    if (!isAiAvailable()) {
      return undefined
    }
    if (!currentPage || !currentVersion) {
      return undefined
    }
    return async (targetUuid: string): Promise<EvaluateResult | undefined> => {
      const fn = buildEvaluateRow(targetUuid)
      if (!fn) {
        return undefined
      }
      return fn()
    }
  }, [currentPage, currentVersion, buildEvaluateRow])

  const evaluatePage = useMemo(() => {
    if (!isAiAvailable()) {
      return undefined
    }
    if (!currentPage || !currentVersion) {
      return undefined
    }
    const targetUuids = currentPage.rows.map(r => r.targetUuid)
    switch (currentPage.pageType) {
      case 'PROGRESS_OVERVIEW':
        return async (): Promise<EvaluateResult[]> => {
          const response = await evaluateOverview({
            snapshotUuid: currentVersion.snapshotUuid,
            leafUuids: targetUuids,
          })
          return response.result ? [{ uuid: '', ...response.result }] : []
        }
      case 'PROGRESS_REPORT_BY_PROCESS':
        return async (): Promise<EvaluateResult[]> => {
          if (currentPage.config.type !== 'PROGRESS_REPORT_BY_PROCESS') {
            return []
          }
          const response = await evaluateProgressByProcess({
            snapshotUuid: currentVersion.snapshotUuid,
            projectPlanUuids: targetUuids,
          })
          return response.result ?? []
        }
      case 'PROGRESS_REPORT_BY_TEAM':
        return async (): Promise<EvaluateResult[]> => {
          if (currentPage.config.type !== 'PROGRESS_REPORT_BY_TEAM') {
            return []
          }
          const response = await evaluateProgressByTeam({
            snapshotUuid: currentVersion.snapshotUuid,
            teamUuids: targetUuids,
            rootUuid: currentPage.config.rootUuid,
          })
          return response.result ?? []
        }
    }
  }, [
    currentPage,
    currentVersion,
    evaluateOverview,
    evaluateProgressByProcess,
    evaluateProgressByTeam,
  ])

  return { evaluatePage, evaluateRow }
}
